import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useDirection } from '../hooks/useDirection';
import { CurrencySelector } from './CurrencySelector';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';

import {
  Bars3Icon,
  XMarkIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  LanguageIcon,
  SparklesIcon,
  BuildingOfficeIcon,
  HomeIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

export const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { isRTL } = useDirection();
  const history = useHistory();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  const handleLogout = () => {
    logout();
    history.push('/');
    setIsProfileMenuOpen(false);
  };

  const navigationItems = [
    {
      name: t('nav.home'),
      href: '/',
      icon: HomeIcon,
    },

  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100'
          : 'bg-white/50 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center h-20">
          {/* Logo Section */}
          <div
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center">
              <img src="/newlogo/newlogonobackground.png" alt="Tripflx" className="h-10 sm:h-12 w-auto scale-[1.5] origin-center object-contain drop-shadow-md " />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-8`}>
            {navigationItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className="group flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                >
                  <span className="font-medium text-base">{item.name}</span>
                </Link>
              );
            })}

            {/* Currency Selector */}
            <CurrencySelector variant="dark" />

            {/* Support Chat Button */}
            <button
              onClick={() => (window as any).__openSupportChat?.()}
              className="group flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200 font-medium text-sm"
            >
              <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
              <span>{i18n.language === 'ar' ? 'الدعم' : 'Live Support'}</span>
            </button>

            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="group flex items-center space-x-1 text-gray-600 hover:text-blue-600 transition-colors duration-200"
            >
              <LanguageIcon className="h-5 w-5" />
              <span className="font-medium text-sm">
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </span>
            </button>

            {/* User Menu / Auth Buttons */}
            {user ? (
              <div className={`relative ${isRTL ? 'mr-4' : 'ml-4'}`}>
                <div className="flex items-center bg-gray-50 rounded-full border border-gray-200 hover:border-blue-200 transition-colors">
                  <Link
                    to="/profile"
                    className={`flex items-center gap-2 pl-1 pr-3 py-1 hover:bg-blue-50 ${isRTL ? 'rounded-r-full' : 'rounded-l-full'} transition-all duration-200 group`}
                  >
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors">
                      <UserIcon className="h-5 w-5" />
                    </div>
                    <span className="text-gray-700 font-medium max-w-[100px] truncate group-hover:text-blue-700">{user.name}</span>
                  </Link>
                  <div className="h-6 w-px bg-gray-200"></div>
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className={`px-2 py-2.5 hover:bg-blue-50 ${isRTL ? 'rounded-l-full' : 'rounded-r-full'} transition-colors`}
                  >
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 hover:text-blue-500 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                {/* @ts-ignore */}
                  {isProfileMenuOpen ? (
                    <div
                      key="profile-menu"
                      className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden py-1 z-50`}
                    >
                      <div className="px-4 py-3 border-b border-gray-50 bg-gray-50/50">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>

                      <div className="p-1">
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-colors"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          <UserIcon className="h-4 w-4 mr-3" />
                          {t('nav.profile')}
                        </Link>

                        {user.role === 'admin' && (
                          <Link
                            to="/admin/dashboard"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-xl transition-colors"
                            onClick={() => setIsProfileMenuOpen(false)}
                          >
                            <SparklesIcon className="h-4 w-4 mr-3" />
                            Admin Dashboard
                          </Link>
                        )}

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                        >
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  ) : null}
              </div>
            ) : (
              <div className={`flex items-center space-x-4 ${isRTL ? 'space-x-reverse mr-4' : 'ml-4'}`}>
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-blue-700 font-medium transition-colors duration-200"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-full shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-xl bg-gray-50 text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {/* @ts-ignore */}
        {isMenuOpen ? (
          <div
            key="mobile-menu"
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 overflow-hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}

              <button
                onClick={toggleLanguage}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <LanguageIcon className="h-5 w-5" />
                <span className="font-medium">
                  {i18n.language === 'en' ? 'العربية' : 'English'}
                </span>
              </button>

              {/* Currency Selector */}
              <div className="px-4 py-2">
                <CurrencySelector variant="dark" />
              </div>

              {/* Support Chat in mobile menu */}
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  (window as any).__openSupportChat?.();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl bg-blue-50 text-blue-600 font-medium"
              >
                <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />
                <span>{i18n.language === 'ar' ? 'الدعم المباشر' : 'Live Support Chat'}</span>
              </button>

              <div className="border-t border-gray-100 my-2 pt-2">
                {user ? (
                  <>
                    <div className="px-4 py-2 mb-2">
                      <p className="font-medium text-gray-900">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <UserIcon className="h-5 w-5" />
                      <span className="font-medium">{t('nav.profile')}</span>
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin/dashboard"
                        className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <SparklesIcon className="h-5 w-5" />
                        <span className="font-medium">Admin Dashboard</span>
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      <span className="font-medium">{t('nav.logout')}</span>
                    </button>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-3 px-4 mt-4">
                    <Link
                      to="/login"
                      className="flex justify-center items-center px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.login')}
                    </Link>
                    <Link
                      to="/register"
                      className="flex justify-center items-center px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {t('nav.register')}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

    </nav>
  );
};
